import { useClerk, useUser } from "@clerk/clerk-react";
import { cn } from "cn";
import type { ReactNode } from "react";
import { Button } from "@/components/ui/button/button";
import { BwLogoutLine } from "@/components/ui/icons";
import PublicFooter from "@/components/ui/layout/public-layout/public-footer";
import { PublicHeader } from "@/components/ui/layout/public-layout/public-header";
import { clearMyPlaceIdStorage } from "@/features/my/shell";
import { useSupportChat } from "@/features/shared/support-chat";
import { PRESALE_EVENTS, useAnalytics } from "@/libs/analytics";
import {
  PRESALE_STEP_COUNT,
  PRESALE_STEPS,
  type PresaleStepKey,
  presaleStepNumber,
} from "../../_libs/presale-funnel";

type PresaleFunnelLayoutProps = {
  step: PresaleStepKey;
  /** Omitted on steps that render their own heading (the out-of-area screen). */
  title?: ReactNode;
  description?: ReactNode;
  /** A back affordance, rendered between the step bar and the heading. */
  back?: ReactNode;
  /** The checkout step's two-column grid needs the full member-checkout width. */
  wide?: boolean;
  /** An optional right-hand column (the sign-up slider); `md:` and up only. */
  aside?: ReactNode;
  children: ReactNode;
};

/**
 * The chrome every funnel step shares — the same `PublicHeader` / `PublicFooter`
 * (and pinned light theme) the member funnel wraps its steps in, plus the presale
 * step counter and heading block. Steps own only their form, so the progress
 * indicator cannot drift between them.
 */
export function PresaleFunnelLayout({
  step,
  title,
  description,
  back,
  wide,
  aside,
  children,
}: PresaleFunnelLayoutProps) {
  const current = presaleStepNumber(step);
  const { isSignedIn } = useUser();
  const { signOut } = useClerk();
  const { resetAnalytics, sendEvent } = useAnalytics();
  const { shutdown } = useSupportChat();

  const handleSignOut = () => {
    if (typeof window !== "undefined") clearMyPlaceIdStorage();
    sendEvent(PRESALE_EVENTS.SIGN_OUT, {
      flowStage: "presale",
      stepId: "sign_out",
      stepName: "Sign Out",
    });
    resetAnalytics(); // reset analytics on sign out
    shutdown();
    signOut({ redirectUrl: `${window.location.origin}/sign-in` });
  };

  const body = (
    <>
      <PresaleStepBar current={current} />
      {back && <div className="mt-6">{back}</div>}
      {(title || description) && (
        <div className={cn("space-y-2", back ? "mt-4" : "mt-6")}>
          {title && <h1 className="text-3xl font-semibold">{title}</h1>}
          {description && (
            <p className="text-base leading-normal font-normal text-text-dark-high">
              {description}
            </p>
          )}
        </div>
      )}
      <div className="mt-8">{children}</div>
    </>
  );

  return (
    <div className="flex min-h-svh flex-col bg-bg-light" data-theme="light">
      <PublicHeader>
        {isSignedIn && (
          <Button
            kind="ghost"
            variant="neutral"
            onClick={handleSignOut}
            className="flex items-center px-4 py-1.5 bg-bg-dark-min rounded-full gap-2 h-8 text-sm font-medium text-text-dark-max hover:bg-bg-dark-low transition"
          >
            <BwLogoutLine className="w-5 h-5 text-text-dark-max" />
            Sign out
          </Button>
        )}
      </PublicHeader>
      <main
        className={cn(
          /* sorted */
          "mx-auto w-full flex-1",
          "px-3 pb-16",
          "xl:px-4",
          // `md:pb-0` — from `md:` up the slider column runs header-to-footer
          // like `/join/sign-up`; below `md` there is no slider, so keep the gap.
          aside
            ? "flex flex-col max-w-7xl md:pb-0"
            : wide
              ? "max-w-5xl"
              : "max-w-xl",
        )}
      >
        {aside ? (
          <section
            className={cn(
              /* sorted */
              "grow",
              "md:grid md:grid-cols-2 lg:gap-6",
            )}
          >
            <div
              className={cn(
                /* sorted */
                "flex flex-col flex-1",
                "mx-auto w-full max-w-xl",
                "md:max-w-sm md:justify-center",
                "mt-12 px-3 md:mt-0 md:px-2",
              )}
            >
              {body}
            </div>
            <div
              className={cn(
                /* sorted */
                "hidden",
                "md:block",
              )}
            >
              {aside}
            </div>
          </section>
        ) : (
          body
        )}
      </main>
      <PublicFooter />
    </div>
  );
}

/** "Step 2 of 3" plus a segment per step, filled up to the current one. */
function PresaleStepBar({ current }: { current: number }) {
  return (
    <div className="mt-8 space-y-3">
      <div className="flex items-center justify-between text-sm text-text-dark-high">
        <span>
          Step {current} of {PRESALE_STEP_COUNT}
        </span>
        <span className="font-medium text-text-primary">
          {PRESALE_STEPS[current - 1]?.label}
        </span>
      </div>
      <ol className="flex gap-2" aria-label="Presale progress">
        {PRESALE_STEPS.map((stepItem, index) => (
          <li
            key={stepItem.key}
            aria-current={index + 1 === current ? "step" : undefined}
            className={cn(
              /* sorted */
              "h-1.5 flex-1 rounded-full",
              index + 1 <= current ? "bg-bg-primary-max" : "bg-bg-dark-min",
            )}
          >
            <span className="sr-only">{stepItem.label}</span>
          </li>
        ))}
      </ol>
    </div>
  );
}
